import type { AccountId, AgentTask, BenchmarkNote, PostScore, ScoreSetting, StyleGuideVersion } from "../types";
import { isAccountId } from "./accounts";

const TEXT = 2000;
const TITLE = 300;

export interface GuideFields {
  titleFormula: string;
  coverLayout: string;
  voice: string;
  aiPractice: string;
  banned: string;
  goodTitles: string;
  badTitles: string;
}

export interface BreakdownFields {
  titlePattern: string;
  coverType: string;
  bodyStructure: string;
  aiTools: string;
  interaction: string;
  whyViral: string;
}

export interface NoteDraft {
  link: string;
  platform: string;
  title: string;
  author: string;
  likes: string;
  collects: string;
  comments: string;
  recordedDate: string;
  coverUrl: string;
  note: string;
}

export type NoteError = "title" | "date" | "link" | "cover" | "count";

export function isRecordedDate(value: unknown): value is string {
  return typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value);
}

/** Empty stays empty. A real count is a non-negative integer, capped so a stray paste cannot explode. */
export function metric(value: unknown): number | null {
  if (value == null || value === "") return null;
  const number = typeof value === "number" ? value : typeof value === "string" && /^\d+$/.test(value.trim()) ? Number(value) : Number.NaN;
  if (!Number.isFinite(number) || number < 0) return null;
  return Math.min(1_000_000_000, Math.round(number));
}

export function metricText(value: string): number | null | "bad" {
  const text = value.trim();
  if (!text) return null;
  const parsed = metric(text);
  return parsed == null ? "bad" : parsed;
}

function clip(value: unknown, max = TEXT): string {
  return typeof value === "string" ? value.trim().slice(0, max) : "";
}

function httpUrl(value: string): boolean {
  return value.startsWith("https://") || value.startsWith("http://");
}

export function noteFromFind(
  task: Pick<AgentTask, "id" | "accountId">,
  input: {
    title?: string;
    link?: string;
    platform?: string;
    author?: string;
    likes?: unknown;
    collects?: unknown;
    comments?: unknown;
    recordedDate?: string;
    coverUrl?: string;
    note?: string;
  },
  now: string,
  id: string,
): BenchmarkNote | null {
  const title = clip(input.title, TITLE);
  const recordedDate = (input.recordedDate ?? "").trim();
  if (!title || !isRecordedDate(recordedDate)) return null;
  return {
    id,
    accountId: isAccountId(task.accountId) ? task.accountId : "xiaohongshu",
    link: clip(input.link, 1000),
    platform: clip(input.platform, 40) || "小红书",
    title,
    author: clip(input.author, 80),
    likes: metric(input.likes),
    collects: metric(input.collects),
    comments: metric(input.comments),
    recordedDate,
    coverUrl: clip(input.coverUrl, 1000),
    note: clip(input.note),
    titlePattern: "",
    coverType: "",
    bodyStructure: "",
    aiTools: "",
    interaction: "",
    whyViral: "",
    sourceTaskId: task.id || null,
    createdAt: now,
    updatedAt: now,
    deletedAt: null,
  };
}

export function manualBenchmark(input: NoteDraft, accountId: AccountId, now: string, id: string): { note: BenchmarkNote } | { error: NoteError } {
  const likes = metricText(input.likes);
  const collects = metricText(input.collects);
  const comments = metricText(input.comments);
  if (likes === "bad" || collects === "bad" || comments === "bad") return { error: "count" };
  const link = input.link.trim();
  const cover = input.coverUrl.trim();
  if (link && !httpUrl(link)) return { error: "link" };
  if (cover && !cover.startsWith("https://")) return { error: "cover" };
  if (!input.title.trim()) return { error: "title" };
  if (!isRecordedDate(input.recordedDate.trim())) return { error: "date" };
  const note = noteFromFind(
    { id: "", accountId },
    {
      title: input.title,
      link,
      platform: input.platform,
      author: input.author,
      likes,
      collects,
      comments,
      recordedDate: input.recordedDate.trim(),
      coverUrl: cover,
      note: input.note,
    },
    now,
    id,
  );
  return note ? { note } : { error: "title" };
}

export function applyBreakdown(note: BenchmarkNote, raw: Partial<BreakdownFields>, now: string): BenchmarkNote {
  return {
    ...note,
    titlePattern: "titlePattern" in raw ? clip(raw.titlePattern) : note.titlePattern,
    coverType: "coverType" in raw ? clip(raw.coverType) : note.coverType,
    bodyStructure: "bodyStructure" in raw ? clip(raw.bodyStructure) : note.bodyStructure,
    aiTools: "aiTools" in raw ? clip(raw.aiTools) : note.aiTools,
    interaction: "interaction" in raw ? clip(raw.interaction) : note.interaction,
    whyViral: "whyViral" in raw ? clip(raw.whyViral) : note.whyViral,
    updatedAt: now,
  };
}

export function breakdownFilled(note: Pick<BenchmarkNote, keyof BreakdownFields>): boolean {
  return Boolean(note.titlePattern || note.coverType || note.bodyStructure || note.aiTools || note.interaction || note.whyViral);
}

export function emptyGuide(): GuideFields {
  return { titleFormula: "", coverLayout: "", voice: "", aiPractice: "", banned: "", goodTitles: "", badTitles: "" };
}

export function guideFields(guide: StyleGuideVersion | null): GuideFields {
  if (!guide) return emptyGuide();
  return {
    titleFormula: guide.titleFormula,
    coverLayout: guide.coverLayout,
    voice: guide.voice,
    aiPractice: guide.aiPractice,
    banned: guide.banned,
    goodTitles: guide.goodTitles,
    badTitles: guide.badTitles,
  };
}

export function guideIsEmpty(fields: GuideFields): boolean {
  return Object.values(fields).every((value) => !value.trim());
}

function trimGuide(fields: GuideFields): GuideFields {
  return {
    titleFormula: clip(fields.titleFormula),
    coverLayout: clip(fields.coverLayout),
    voice: clip(fields.voice),
    aiPractice: clip(fields.aiPractice),
    banned: clip(fields.banned),
    goodTitles: clip(fields.goodTitles),
    badTitles: clip(fields.badTitles),
  };
}

export function appendStyleVersion(
  guides: readonly StyleGuideVersion[],
  accountId: AccountId,
  fields: GuideFields,
  now: string,
  id: string,
): StyleGuideVersion[] {
  const next: StyleGuideVersion = {
    id,
    accountId,
    ...trimGuide(fields),
    createdAt: now,
    updatedAt: now,
    current: true,
  };
  return [
    next,
    ...guides.map((guide) => (guide.accountId === accountId && guide.current ? { ...guide, current: false, updatedAt: now } : guide)),
  ];
}

export function markStyleCurrent(guides: readonly StyleGuideVersion[], id: string, now: string): StyleGuideVersion[] {
  const target = guides.find((guide) => guide.id === id);
  if (!target) return [...guides];
  return guides.map((guide) => {
    if (guide.accountId !== target.accountId) return guide;
    if (guide.id === id) return { ...guide, current: true, updatedAt: now };
    if (guide.current) return { ...guide, current: false, updatedAt: now };
    return guide;
  });
}

export function currentGuide(guides: readonly StyleGuideVersion[], accountId: string): StyleGuideVersion | null {
  return (
    guides
      .filter((guide) => guide.accountId === accountId && guide.current)
      .sort((left, right) => right.updatedAt.localeCompare(left.updatedAt))[0] ?? null
  );
}

export function clampScore(value: unknown): number | null {
  if (typeof value !== "number" || !Number.isFinite(value)) return null;
  return Math.max(0, Math.min(10, Math.round(value)));
}

export function scoreTotal(score: Pick<PostScore, "sense" | "ai" | "saveValue" | "cover">): number {
  return score.sense + score.ai + score.saveValue + score.cover;
}

export function clampPass(value: unknown): number {
  if (typeof value !== "number" || !Number.isFinite(value)) return 28;
  return Math.max(0, Math.min(40, Math.round(value)));
}

export function passed(total: number, line: number): boolean {
  return total >= line;
}

export function passFor(settings: readonly ScoreSetting[], accountId: string): number {
  const row = settings.find((item) => item.accountId === accountId);
  return row ? clampPass(row.passTotal) : 28;
}

export function latestScore(scores: readonly PostScore[], topicId: string): PostScore | null {
  return scores.filter((score) => score.topicId === topicId).sort((left, right) => right.createdAt.localeCompare(left.createdAt))[0] ?? null;
}

export function rewriteComment(score: PostScore, line: number): string {
  const total = scoreTotal(score);
  return [
    `网感 ${score.sense}/10：${score.senseReason.trim() || "没有说明"}`,
    `AI相关度 ${score.ai}/10：${score.aiReason.trim() || "没有说明"}`,
    `收藏价值 ${score.saveValue}/10：${score.saveReason.trim() || "没有说明"}`,
    `封面吸引力 ${score.cover}/10：${score.coverReason.trim() || "没有说明"}`,
    `合计 ${total}/40，未达到 ${line}。`,
  ].join("\n");
}

export function scoreFromParts(
  input: {
    accountId: string;
    topicId: string;
    styleVersionId?: string | null;
    sense: unknown;
    ai: unknown;
    saveValue: unknown;
    cover: unknown;
    senseReason?: unknown;
    aiReason?: unknown;
    saveReason?: unknown;
    coverReason?: unknown;
  },
  now: string,
  id: string,
): PostScore | null {
  const sense = clampScore(input.sense);
  const ai = clampScore(input.ai);
  const saveValue = clampScore(input.saveValue);
  const cover = clampScore(input.cover);
  if (sense == null || ai == null || saveValue == null || cover == null) return null;
  if (!input.topicId.trim()) return null;
  return {
    id,
    accountId: isAccountId(input.accountId) ? input.accountId : "xiaohongshu",
    topicId: input.topicId.trim().slice(0, 80),
    styleVersionId: input.styleVersionId?.trim() ? input.styleVersionId.trim().slice(0, 80) : null,
    sense,
    ai,
    saveValue,
    cover,
    senseReason: clip(input.senseReason),
    aiReason: clip(input.aiReason),
    saveReason: clip(input.saveReason),
    coverReason: clip(input.coverReason),
    createdAt: now,
    updatedAt: now,
  };
}

export function normalizeBenchmark(value: unknown): BenchmarkNote | null {
  if (!value || typeof value !== "object") return null;
  const raw = value as Partial<BenchmarkNote>;
  if (typeof raw.id !== "string" || typeof raw.updatedAt !== "string" || typeof raw.title !== "string") return null;
  if (!isRecordedDate(raw.recordedDate)) return null;
  return {
    id: raw.id,
    accountId: isAccountId(raw.accountId) ? raw.accountId : "xiaohongshu",
    link: clip(raw.link, 1000),
    platform: clip(raw.platform, 40) || "小红书",
    title: raw.title.trim().slice(0, TITLE),
    author: clip(raw.author, 80),
    likes: metric(raw.likes),
    collects: metric(raw.collects),
    comments: metric(raw.comments),
    recordedDate: raw.recordedDate,
    coverUrl: clip(raw.coverUrl, 1000),
    note: clip(raw.note),
    titlePattern: clip(raw.titlePattern),
    coverType: clip(raw.coverType),
    bodyStructure: clip(raw.bodyStructure),
    aiTools: clip(raw.aiTools),
    interaction: clip(raw.interaction),
    whyViral: clip(raw.whyViral),
    sourceTaskId: typeof raw.sourceTaskId === "string" ? raw.sourceTaskId : null,
    createdAt: typeof raw.createdAt === "string" ? raw.createdAt : raw.updatedAt,
    updatedAt: raw.updatedAt,
    deletedAt: typeof raw.deletedAt === "string" ? raw.deletedAt : null,
  };
}

export function normalizeGuide(value: unknown): StyleGuideVersion | null {
  if (!value || typeof value !== "object") return null;
  const raw = value as Partial<StyleGuideVersion>;
  if (typeof raw.id !== "string" || typeof raw.updatedAt !== "string") return null;
  const current = (value as { current?: unknown }).current;
  return {
    id: raw.id,
    accountId: isAccountId(raw.accountId) ? raw.accountId : "xiaohongshu",
    titleFormula: clip(raw.titleFormula),
    coverLayout: clip(raw.coverLayout),
    voice: clip(raw.voice),
    aiPractice: clip(raw.aiPractice),
    banned: clip(raw.banned),
    goodTitles: clip(raw.goodTitles),
    badTitles: clip(raw.badTitles),
    createdAt: typeof raw.createdAt === "string" ? raw.createdAt : raw.updatedAt,
    updatedAt: raw.updatedAt,
    current: current === true || current === 1,
  };
}

export function normalizeScore(value: unknown): PostScore | null {
  if (!value || typeof value !== "object") return null;
  const raw = value as Partial<PostScore>;
  if (typeof raw.id !== "string" || typeof raw.topicId !== "string" || typeof raw.updatedAt !== "string") return null;
  const score = scoreFromParts(
    {
      accountId: typeof raw.accountId === "string" ? raw.accountId : "",
      topicId: raw.topicId,
      styleVersionId: raw.styleVersionId,
      sense: raw.sense,
      ai: raw.ai,
      saveValue: raw.saveValue,
      cover: raw.cover,
      senseReason: raw.senseReason,
      aiReason: raw.aiReason,
      saveReason: raw.saveReason,
      coverReason: raw.coverReason,
    },
    typeof raw.createdAt === "string" ? raw.createdAt : raw.updatedAt,
    raw.id,
  );
  return score ? { ...score, createdAt: typeof raw.createdAt === "string" ? raw.createdAt : raw.updatedAt, updatedAt: raw.updatedAt } : null;
}

export function normalizeSetting(value: unknown): ScoreSetting | null {
  if (!value || typeof value !== "object") return null;
  const raw = value as Partial<ScoreSetting>;
  const accountId = isAccountId(raw.accountId) ? raw.accountId : isAccountId(raw.id) ? raw.id : null;
  if (!accountId || typeof raw.updatedAt !== "string") return null;
  return { id: accountId, accountId, passTotal: clampPass(raw.passTotal), updatedAt: raw.updatedAt };
}
